import React from "react";
import './StartPage.css';
import {Connector, Status} from "../../Model/Charger";
import {formatTariff} from "../../Common/Tariff";
// Inlined rather than loaded via <img>, so the selected state doesn't flash while its SVG downloads
import {ReactComponent as CheckedButton} from "../../assets/checked_button.svg";
import {ReactComponent as UncheckedButton} from "../../assets/unchecked_button.svg";

// Free, or with the driver's cable already in it. Anything else cannot be paid for.
const PICKABLE_STATUSES: Status[] = [
    Status.AVAILABLE,
    Status.PREPARING
];

// In use or held for someone else rather than faulty, so it reads as "Opptatt"
const BUSY_STATUSES: Status[] = [
    Status.OCCUPIED,
    Status.CHARGING,
    Status.DISCHARGING,
    Status.PAUSED,
    Status.FINISHING,
    Status.RESERVED
];

// PREPARING means a cable is already plugged into this connector, so it is the
// one the driver standing at the charger means to pay for
export function isPreparing(connector: Connector): boolean {
    return connector.status === Status.PREPARING;
}

export function isSelectable(connector: Connector): boolean {
    return PICKABLE_STATUSES.includes(connector.status);
}

// FAULTED, UNAVAILABLE, NEW, IN_PROVISION and UNKNOWN all mean the connector
// cannot take a car. So does a missing or unrecognised value: anything that
// cannot be placed counts as out of order.
export function isBroken(connector: Connector): boolean {
    return !PICKABLE_STATUSES.includes(connector.status) && !BUSY_STATUSES.includes(connector.status);
}

// What a connector that cannot be picked says instead of the pick button
function statusLabel(connector: Connector): string {
    if (connector.status === Status.UNAVAILABLE) {
        return "Utilgjengelig";
    }
    return isBroken(connector) ? "I ustand" : "Opptatt";
}

export default function ConnectorCard({connector, setSelectedChargerId, selected}:
                                     { connector: Connector, setSelectedChargerId: any, selected: boolean }) {

    const isAvailable = isSelectable(connector);
    const preparing = isPreparing(connector);

    return (
        <section onClick={() => {
            if (isAvailable) {
                setSelectedChargerId(connector.id)
            }
        }} className={"connector-card connector-row" + (isAvailable ? " connector-card--tappable" : " connector-row--disabled")}
            data-demo="choose-charger">
            <div className="connector-row-info">
                {preparing ? <>
                    {/* A cable is plugged in here, so say so where the eye lands first */}
                    <p className="connector-row-title">
                        <span className="connector-row-id">{connector.smsCode}</span> &middot; <span className="connector-row-plugged">Tilkoblet</span>
                    </p>
                    <p className="connector-row-price">{connector.type} &middot; {connector.maxEffect} kW</p>
                </> :
                    <p className="connector-row-title">
                        <span className="connector-row-id">{connector.smsCode}</span> &middot; {connector.type} &middot; {connector.maxEffect} kW
                    </p>
                }
                <p className="connector-row-price">Pris: {formatTariff(connector.tariff_Unreg)}</p>
            </div>

            {selected &&
                <CheckedButton className="connector-row-check" role="img" aria-label="Valgt"/>
            }
            {!selected && isAvailable &&
                <button type="button" className="connector-row-pick">
                    <UncheckedButton role="img" aria-label="Velg"/>
                </button>
            }
            {!selected && !isAvailable &&
                <span className="connector-row-status">{statusLabel(connector)}</span>
            }
        </section>
    );
}
